import { determineCityComponent } from '../../../../global/utils/commonUtils';
import {
    getLocationsDetailsByPlaceId,
    geoCodeByLatLong
} from '../../../../global/modules/location-autocomplete/api/getLocations';
import { logError } from '../../../../global/utils/logger';
import axiosInstance from '../../../../../api/axiosDotComInstance';
import { getFormTitle } from '../../../util/ccaUtil';
import { ADDRESS_COMPONENT_TYPE, ADDRESS_COMPONENTS } from '../../../../../constants/cartConstants';
import { ERROR_MSG } from '../../../../../constants/errorMappingCodes';
import { API_CONFIG } from '../../../../../constants/apiConfig';
import { BUSINESS_BILLING_FORM_ERRORS, CCAConstant, STEP_NAMES } from '../../../constants';
import { EVENT_ECOMMERCE_NAMES_CONFIG } from '../../../../../constants/analyticsConstants/Ecommerce';
import { STORAGE_CONFIG } from '../../../../../constants/storageConfig';

const determineTypeOfAddress = component => {
    const { types = [] } = component || {};
    if (types.includes(ADDRESS_COMPONENTS.SUBLOCALITY)) {
        return ADDRESS_COMPONENT_TYPE.SUBLOCALITY;
    }
    if (
        types.includes(ADDRESS_COMPONENTS.LOCALITY) ||
        types.includes(ADDRESS_COMPONENTS.AL3) ||
        types.includes(ADDRESS_COMPONENTS.AL2)
    ) {
        return ADDRESS_COMPONENT_TYPE.CITY;
    }
    if (types.includes(ADDRESS_COMPONENTS.AL1)) {
        return ADDRESS_COMPONENT_TYPE.STATE;
    }
    if (types.includes(ADDRESS_COMPONENTS.POSTAL)) {
        return ADDRESS_COMPONENT_TYPE.POSTAL_CODE;
    }
    if (types.includes(ADDRESS_COMPONENTS.STREET_NO)) {
        return ADDRESS_COMPONENT_TYPE.STREET;
    }
    if (types.includes(ADDRESS_COMPONENTS.COUNTRY)) {
        return ADDRESS_COMPONENT_TYPE.COUNTRY;
    }
    if (types.includes(ADDRESS_COMPONENTS.ROUTE)) {
        return ADDRESS_COMPONENT_TYPE.ROUTE;
    }
    if (types.includes(ADDRESS_COMPONENTS.NEIGHBORHOOD)) {
        return ADDRESS_COMPONENT_TYPE.NEIGHBORHOOD;
    }
};

const extractAddressComponents = details => {
    let computedAddress = {
        addr2: '',
        city: '',
        state: '',
        zipCode: ''
    };
    const { addressComponents = [] } = details || {};
    let citySelected = false;
    if (addressComponents?.length > 0) {
        addressComponents?.map(component => {
            const type = determineTypeOfAddress(component);
            switch (type) {
                case ADDRESS_COMPONENT_TYPE.CITY: {
                    computedAddress.city = determineCityComponent(addressComponents);
                    citySelected = true;
                    break;
                }

                case ADDRESS_COMPONENT_TYPE.STATE: {
                    computedAddress.state = component.shortName;
                    break;
                }

                case ADDRESS_COMPONENT_TYPE.POSTAL_CODE: {
                    computedAddress.zipCode = component.longName;
                    break;
                }
                case ADDRESS_COMPONENT_TYPE.AREA_L2: {
                    computedAddress.addr2 = component.longName;
                    break;
                }
                case ADDRESS_COMPONENT_TYPE.SUBLOCALITY: {
                    if (!citySelected) {
                        computedAddress.city = component.longName;
                    }
                    break;
                }
                case ADDRESS_COMPONENT_TYPE.COUNTRY: {
                    break;
                }
                default:
                    break;
            }
        });
    }
    return computedAddress;
};

const getZipFromLatLong = details => {
    let zip = '';
    try {
        const { addressComponents = [] } = details || {};
        if (addressComponents?.length > 0) {
            addressComponents?.map(component => {
                const type = determineTypeOfAddress(component);
                if (type == ADDRESS_COMPONENT_TYPE.POSTAL_CODE) {
                    zip = component.longName;
                    return;
                }
            });
        }
    } catch (error) {
        logError(error?.message || ERROR_MSG?.INTERNAL_SERVER_ERROR, false, 'getZipFromLatLong', [details]);
    }
    return zip;
};

export const handleAutoAddress = async (selectedPlaceId, selectedAutoAddress, countryId) => {
    let addObj = {
        address: selectedAutoAddress,
        city: '',
        state: '',
        zipCode: ''
    };

    try {
        const { data: { result = {} } = {} } = await getLocationsDetailsByPlaceId(selectedPlaceId, countryId);
        const { geometry: { location: { lat = '', lng = '' } = {} } = {} } = result;
        let { city, state, zipCode } = extractAddressComponents(result);

        if (!zipCode) {
            const latlng = `${lat},${lng}`;
            const geocoderesponse = await geoCodeByLatLong(latlng);
            zipCode = getZipFromLatLong(geocoderesponse?.data?.results[0]);
        }
        addObj = {
            address: selectedAutoAddress,
            city,
            state,
            zipCode
        };
    } catch (error) {
        logError(error?.message || ERROR_MSG?.INTERNAL_SERVER_ERROR, false, 'getZipFromLatLong', [
            selectedPlaceId,
            selectedAutoAddress
        ]);
    }

    return addObj;
};

export const getAVSValidationAPI = async (addressObj, companyID, type) => {
    try {
        const { data, error } = await axiosInstance.post(
            API_CONFIG.AVS_VALIDATION_URL,
            {
                address1: addressObj?.address,
                address2: addressObj?.addressLine2,
                city: addressObj?.city,
                state: addressObj?.state,
                zip: addressObj?.zipCode,
                type: type
            },
            {
                headers: {
                    companyId: companyID
                }
            }
        );
        return { data, error };
    } catch (error) {
        return { error };
    }
};

export const checkValidAddress = async (values, errors, companyId, sendEventsForCcaStepFormError) => {
    const addressValidationPromise = validateAddress(
        values?.address,
        values?.addressLine2,
        values?.city,
        values?.state,
        values?.zipCode,
        'address',
        errors,
        companyId,
        sendEventsForCcaStepFormError,
        'delivery'
    );
    const billingAddressValidationPromise = !values.isAddressSameCheckbox
        ? validateAddress(
              values?.billingAddress,
              values?.billingAddressLine2,
              values?.billingCity,
              values?.billingState,
              values?.billingZipCode,
              'billingAddress',
              errors,
              companyId,
              sendEventsForCcaStepFormError,
              'mailable'
          )
        : null;
    // Await both promises simultaneously
    await Promise.all([addressValidationPromise, billingAddressValidationPromise]);
};

const validateAddress = async (
    address,
    addressLine2,
    city,
    state,
    zipCode,
    errorKey,
    errors,
    companyId,
    sendEventsForCcaStepFormError,
    type
) => {
    const addressObj = {
        address,
        addressLine2,
        city,
        state,
        zipCode
    };
    const avsResp = await getAVSValidationAPI(addressObj, companyId, type);
    const { data: { data: { addressInfo: { addrResult = '', addMessage = '' } = {} } = {} } = {} } = avsResp || {};
    const isValidAddress = addrResult === '0' || addrResult === '1';
    if (!isValidAddress) {
        if (errorKey === 'address') {
            errors[errorKey] = BUSINESS_BILLING_FORM_ERRORS.ERROR_VALID_ADDRESS;
        } else {
            if (addMessage && addMessage != '') {
                errors[errorKey] = addMessage;
            } else {
                errors[errorKey] = BUSINESS_BILLING_FORM_ERRORS.ERROR_VALID_BILLING_ADDRESS;
            }
        }
        const ccaSubmissionId = sessionStorage?.getItem(STORAGE_CONFIG.SESSION_STORAGE.CCA_SUBMISSION_ID);
        sendEventsForCcaStepFormError(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CCA_FORM_ERROR,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CCA_FORM_NAME,
            BUSINESS_BILLING_FORM_ERRORS.ERROR_VALID_ADDRESS,
            errorKey,
            getFormTitle(STEP_NAMES[CCAConstant.TWO]),
            window.location.href,
            CCAConstant.THREE,
            ccaSubmissionId
        );
    }
};
